import { api } from "./client";

/* ──────────────────────────────────────────────────────────────────────
   Mirrors api/routers/print_search.py — keep in sync.

   Full-text search over OCR'd newspaper articles (Elasticsearch alias
   `print-articles`). SuperAdmin only; the API 403s everyone else.

   `headline_html`, `snippets_html` and `body_html` are server-sanitised:
   everything is HTML-escaped and <mark>…</mark> is the only markup left,
   so they are safe for dangerouslySetInnerHTML.
   ────────────────────────────────────────────────────────────────────── */

export const SEARCH_MODES = ["any", "phrase", "case", "exact"] as const;
export type SearchMode = (typeof SEARCH_MODES)[number];
export const SEARCH_SORTS = ["relevance", "newest", "circulation"] as const;
export type SearchSort = (typeof SEARCH_SORTS)[number];

export const FACET_KEYS = ["category", "language", "channel", "edition", "state", "kind", "page"] as const;
export type FacetKey = (typeof FACET_KEYS)[number];

export interface SearchParams {
  q: string;
  mode: SearchMode;
  sort: SearchSort;
  page: number;
  size: number;
  date_from?: string;
  date_to?: string;
  min_chars?: number;
  city?: string;
  /** one card per story (same publisher + day + headline across editions); default on */
  group: boolean;
  filters: Partial<Record<FacetKey, string[]>>;
}

/** facet key → API query-param name */
export const FACET_PARAM: Record<FacetKey, string> = {
  category: "category",
  language: "language",
  channel: "channel_id",
  edition: "edition_id",
  state: "state",
  kind: "kind",
  page: "page_no",
};

export interface FacetBucket { value: string; label: string; count: number }

/** One edition of a grouped story (inner hit). */
export interface StoryEdition {
  article_id: number;
  edition_id: number | null;
  edition_name: string | null;
  city: string | null;
  page_no: number | null;
  display_page_no: string | null;
  word_count: number | null;
  crop_url: string | null;
  page_url: string | null;
}

export interface SearchHit {
  article_id: number;
  article_no: number | null;
  business_date: string | null;
  channel_id: number | null;
  channel_name: string | null;
  publication_code: string | null;
  edition_id: number | null;
  edition_name: string | null;
  edition_alias: string | null;
  city: string | null;
  state: string | null;
  zone: string | null;
  language: string | null;
  category: string | null;
  circulation: number | null;
  page_no: number | null;
  display_page_no: string | null;
  page_count: number | null;
  page_w: number | null;
  page_h: number | null;
  bbox_x: number | null;
  bbox_y: number | null;
  bbox_w: number | null;
  bbox_h: number | null;
  kind: string | null;
  is_lead: boolean | null;
  is_above_fold: boolean | null;
  position_zone: string | null;
  crop_w: number | null;
  crop_h: number | null;
  headline: string | null;
  char_count: number | null;
  word_count: number | null;
  es_indexed_at: string | null;
  story_key: string | null;
  /** only when the search was grouped */
  editions?: StoryEdition[];
  edition_count?: number;
  score: number | null;
  headline_html: string | null;
  snippets_html: string[];
  crop_url: string | null;
  page_url: string | null;
}

export interface SearchResponse {
  q: string;
  mode: SearchMode;
  sort: SearchSort;
  page: number;
  size: number;
  total: number;
  total_relation: string;
  grouped: boolean;
  /** number of distinct stories (approximate above ~40k) when grouped, else null */
  stories: number | null;
  took_ms: number | null;
  hits: SearchHit[];
  facets: Record<FacetKey | "business_date", FacetBucket[]>;
}

/** The full ES document — every field is optional because the index is `dynamic: false`
 *  and older docs may predate a field. */
export type ArticleDoc = Record<string, unknown> & {
  article_id: number;
  issue_id?: number | null;
  headline?: string | null;
  body_text?: string | null;
  full_text?: string | null;
  page_w?: number | null;
  page_h?: number | null;
  bbox_x?: number | null;
  bbox_y?: number | null;
  bbox_w?: number | null;
  bbox_h?: number | null;
};

export interface ArticleResponse {
  doc: ArticleDoc;
  polygon: number[][] | null;
  crop_url: string | null;
  page_url: string | null;
  headline_html: string | null;
  body_html: string | null;
}

export interface StatsResponse {
  index: string;
  total_docs: number;
  by_date: { date: string; count: number }[];
  by_engine: { engine: string; count: number }[];
  avg_crawl_to_indexed_s: number | null;
  avg_ocr_s: number | null;
  last_indexed_at: string | null;
  indexed_last_hour: number;
  publishers: number;
  editions: number;
  took_ms: number | null;
}

/* axios would send arrays as `x[]=1&x[]=2`, which FastAPI does not read — build the
   query string ourselves as repeated keys. */
function toQuery(p: SearchParams): URLSearchParams {
  const u = new URLSearchParams();
  if (p.q) u.set("q", p.q);
  u.set("mode", p.mode);
  u.set("sort", p.sort);
  u.set("page", String(p.page));
  u.set("size", String(p.size));
  if (p.date_from) u.set("date_from", p.date_from);
  if (p.date_to) u.set("date_to", p.date_to);
  if (p.min_chars) u.set("min_chars", String(p.min_chars));
  if (p.city) u.set("city", p.city);
  if (p.group) u.set("group", "1");
  for (const k of FACET_KEYS) {
    for (const v of p.filters[k] ?? []) u.append(FACET_PARAM[k], v);
  }
  return u;
}

export async function searchPrint(p: SearchParams, signal?: AbortSignal): Promise<SearchResponse> {
  const r = await api.get<SearchResponse>(`/api/print-search/search?${toQuery(p).toString()}`, { signal });
  return r.data;
}

export async function fetchPrintArticle(id: number, q: string, mode: SearchMode): Promise<ArticleResponse> {
  const u = new URLSearchParams({ mode });
  if (q) u.set("q", q);
  const r = await api.get<ArticleResponse>(`/api/print-search/article/${id}?${u.toString()}`);
  return r.data;
}

export async function fetchPrintStats(): Promise<StatsResponse> {
  const r = await api.get<StatsResponse>("/api/print-search/stats");
  return r.data;
}
